import React, { useState } from 'react';
import { BookOpen } from 'lucide-react';

function RecommendedReadings({ readings, onAdd, onUpdate, onDelete, selectedReading }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ title: '', author: '', notes: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.title.trim()) {
      if (editingId) {
        onUpdate(editingId, formData.title, formData.author, formData.notes);
        setEditingId(null);
      } else {
        onAdd(formData.title, formData.author, formData.notes);
      }
      setFormData({ title: '', author: '', notes: '' });
      setShowAddForm(false);
    }
  };

  const handleEdit = (reading) => {
    setEditingId(reading.id);
    setFormData({
      title: reading.title,
      author: reading.author || '',
      notes: reading.notes || ''
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({ title: '', author: '', notes: '' });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Detail view when a specific reading is selected
  if (selectedReading) {
    return (
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
        <div className="mb-6">
          <h3 className="text-2xl font-semibold mb-2 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <BookOpen className="w-6 h-6" />
            <span>Reading Details</span>
          </h3>
          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Added {formatDate(selectedReading.created_at)}</p>
        </div>

        {editingId === selectedReading.id ? (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>Title:</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full bg-[var(--bg-primary)] placeholder-gray-500 px-4 py-3 rounded-lg border border-[var(--border-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/50 focus:border-[var(--accent)]/50 font-medium"
                  style={{ color: 'var(--text-primary)' }}
                  placeholder="Book/Article title"
                  required
                />
              </div>
              <div>
                <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>Author:</label>
                <input
                  type="text"
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  className="w-full bg-[var(--bg-primary)] placeholder-gray-500 px-4 py-3 rounded-lg border border-[var(--border-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/50 focus:border-[var(--accent)]/50 font-medium"
                  style={{ color: 'var(--text-primary)' }}
                  placeholder="Author name"
                />
              </div>
              <div>
                <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>Notes:</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full bg-[var(--bg-primary)] placeholder-gray-500 px-4 py-3 rounded-lg border border-[var(--border-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/50 focus:border-[var(--accent)]/50 resize-none h-32 font-medium"
                  style={{ color: 'var(--text-primary)' }}
                  placeholder="Why recommend this reading?"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="bg-[var(--accent)] hover:bg-[var(--accent)] px-5 py-2.5 rounded-lg text-sm font-semibold transition-all"
                  style={{ color: 'var(--text-primary)' }}
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="bg-[var(--bg-tertiary)] hover:bg-[var(--border-color)] px-5 py-2.5 rounded-lg text-sm font-medium transition-all border border-[var(--border-color)]"
                  style={{ color: 'var(--text-primary)' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        ) : (
          <div>
            <div className="bg-[var(--bg-primary)] p-5 rounded-lg mb-6 border border-[var(--border-color)]">
              <h4 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>{selectedReading.title}</h4>
              {selectedReading.author && (
                <p className="text-base mb-4" style={{ color: 'var(--text-secondary)' }}>by {selectedReading.author}</p>
              )}
              {selectedReading.notes && (
                <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
                  <p className="text-sm mb-2" style={{ color: 'var(--text-tertiary)' }}>Recommendation notes:</p>
                  <p className="text-base leading-relaxed" style={{ color: 'var(--text-primary)' }}>{selectedReading.notes}</p>
                </div>
              )}
            </div>

            {(onUpdate || onDelete) && (
              <div className="flex gap-3">
                {onUpdate && (
                  <button
                    onClick={() => handleEdit(selectedReading)}
                    className="bg-[var(--accent)] hover:bg-[var(--accent)] px-5 py-2.5 rounded-lg text-sm font-semibold transition-all"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    Edit Reading
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={() => {
                      if (window.confirm('Delete this reading?')) {
                        onDelete(selectedReading.id);
                      }
                    }}
                    className="bg-red-500/20 hover:bg-red-500/30 text-red-300 px-5 py-2.5 rounded-lg text-sm font-medium transition-all border border-red-500/30"
                  >
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // List view showing all readings
  return (
    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Reading List</h3>
          <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>{readings.length} recommendations</p>
        </div>
        {onAdd && (
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-[var(--accent)] hover:bg-[var(--accent)] px-4 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{ color: 'var(--text-primary)' }}
          >
            {showAddForm ? 'Cancel' : '+ Add Reading'}
          </button>
        )}
      </div>

      {showAddForm && onAdd && (
        <form onSubmit={handleSubmit} className="mb-6 p-5 bg-[var(--bg-primary)] rounded-lg border border-[var(--border-color)]">
          <div className="space-y-4">
            <div>
              <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>Title:</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full bg-[var(--bg-primary)] placeholder-gray-500 px-4 py-3 rounded-lg border border-[var(--border-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/50 focus:border-[var(--accent)]/50 font-medium"
                  style={{ color: 'var(--text-primary)' }}
                placeholder="Book/Article title"
                required
              />
            </div>
            <div>
              <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>Author:</label>
              <input
                type="text"
                value={formData.author}
                onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  className="w-full bg-[var(--bg-primary)] placeholder-gray-500 px-4 py-3 rounded-lg border border-[var(--border-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/50 focus:border-[var(--accent)]/50 font-medium"
                  style={{ color: 'var(--text-primary)' }}
                placeholder="Author name"
              />
            </div>
            <div>
              <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>Notes:</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full bg-[var(--bg-primary)] placeholder-gray-500 px-4 py-3 rounded-lg border border-[var(--border-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/50 focus:border-[var(--accent)]/50 resize-none h-24 font-medium"
                  style={{ color: 'var(--text-primary)' }}
                placeholder="Why recommend this reading?"
              />
            </div>
            <button
              type="submit"
              className="w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] px-4 py-3 rounded-xl text-sm font-medium transition-all"
              style={{ color: 'var(--text-primary)' }}
            >
              Add Reading
            </button>
          </div>
        </form>
      )}

      {readings.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-base mb-2" style={{ color: 'var(--text-tertiary)' }}>No readings yet</p>
          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Click "+ Add Reading" to recommend books or articles</p>
        </div>
      ) : (
        <div className="space-y-3">
          {readings.map((reading) => (
            <div key={reading.id} className="bg-[var(--bg-primary)] hover:bg-[var(--bg-secondary)] rounded-lg p-4 transition-all border border-[var(--border-color)]">
                <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold text-base" style={{ color: 'var(--text-primary)' }}>{reading.title}</h4>
                {(onUpdate || onDelete) && (
                  <div className="flex gap-2">
                    {onUpdate && (
                      <button
                        onClick={() => handleEdit(reading)}
                        className="text-[var(--accent)] hover:text-[var(--accent)] text-xs transition-colors"
                      >
                        Edit
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={() => {
                          if (window.confirm('Delete this reading?')) {
                            onDelete(reading.id);
                          }
                        }}
                        className="text-red-400 hover:text-red-300 text-xs transition-colors"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
              {reading.author && (
                <p className="text-sm mb-2" style={{ color: 'var(--text-tertiary)' }}>by {reading.author}</p>
              )}
              {reading.notes && (
                <p className="text-sm mt-2 bg-[var(--bg-secondary)] p-3 rounded-lg border border-[var(--border-color)]" style={{ color: 'var(--text-secondary)' }}>{reading.notes}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default RecommendedReadings;
